/**
 * Utility functions for generating JSON-LD structured data.
 * ref: https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data
 */

type JsonLdContext = 'https://schema.org' | string;

interface BaseJsonLd {
  '@context': JsonLdContext;
  '@type': string;
}

type ItemListOrder = 'Unordered' | 'ItemListOrderAscending' | 'ItemListOrderDescending';

interface OrganizationJsonLd extends BaseJsonLd {
  '@type': 'Organization';
  '@id'?: string;
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  sameAs?: string[];
  contactPoint?: {
    '@type': 'ContactPoint';
    telephone?: string;
    contactType?: string;
    email?: string;
  };
}

interface WebSiteJsonLd extends BaseJsonLd {
  '@type': 'WebSite';
  '@id'?: string;
  name?: string;
  url?: string;
  description?: string;
  publisher?: { '@id': string };
  inLanguage?: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

type ArticleType = 'Article' | 'BlogPosting' | 'NewsArticle';

interface ArticleJsonLd extends BaseJsonLd {
  '@type': ArticleType;
  '@id'?: string;
  headline?: string;
  description?: string;
  genre?: string;
  url?: string;
  inLanguage?: string;
  image?: string | string[];
  datePublished?: string;
  dateModified?: string;
  mainEntityOfPage?: { '@id': string };
  author?: {
    '@type': 'Person' | 'Organization';
    '@id'?: string;
    name: string;
    url?: string;
  };
  publisher?: {
    '@type': 'Organization';
    '@id'?: string;
    name?: string;
    logo?: {
      '@type': 'ImageObject';
      url: string;
    };
  };
  about?: {
    '@type': 'Organization';
    name: string;
    url?: string;
  };
}

interface BreadcrumbListJsonLd extends BaseJsonLd {
  '@type': 'BreadcrumbList';
  itemListElement: Array<ListItemJsonLd>;
}

interface FAQPageJsonLd extends BaseJsonLd {
  '@type': 'FAQPage';
  '@id'?: string;
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

interface OfferJsonLd {
  '@type': 'Offer';
  name?: string;
  price?: string | number;
  priceCurrency?: string;
  availability?: string;
  url?: string;
  description?: string;
  priceSpecification?: {
    '@type': 'UnitPriceSpecification';
    price?: string | number;
    priceCurrency?: string;
    billingIncrement?: string;
    billingDuration?: string;
  };
}

interface ProductJsonLd extends BaseJsonLd {
  '@type': 'Product';
  '@id'?: string;
  name?: string;
  description?: string;
  image?: string | string[];
  brand?: { '@id': string } | { '@type': 'Brand'; name: string };
  url?: string;
  offers?: OfferJsonLd | Array<OfferJsonLd>;
}

interface PersonJsonLd extends BaseJsonLd {
  '@type': 'Person';
  name?: string;
  url?: string;
  image?: string;
  jobTitle?: string;
  worksFor?: {
    '@type': 'Organization';
    name: string;
  };
  sameAs?: string[];
}

interface SoftwareApplicationJsonLd extends BaseJsonLd {
  '@type': 'SoftwareApplication';
  '@id'?: string;
  name?: string;
  operatingSystem?: string;
  applicationCategory?: string;
  description?: string;
  url?: string;
  brand?: { '@id': string } | { '@type': 'Brand'; name: string };
  isPartOf?: { '@id': string };
  offers?: {
    '@type': 'Offer';
    url?: string;
    availability?: string;
    priceSpecification?: {
      '@type': 'UnitPriceSpecification';
      price?: string | number;
      priceCurrency?: string;
      billingIncrement?: string;
      billingDuration?: string;
    };
  };
  featureList?: string[];
}

interface WebPageJsonLd extends BaseJsonLd {
  '@type': 'WebPage';
  '@id'?: string;
  url?: string;
  name?: string;
  description?: string;
  inLanguage?: string;
  about?: { '@id': string };
  isPartOf?: { '@id': string };
}

interface WebApplicationJsonLd extends BaseJsonLd {
  '@type': 'WebApplication';
  name?: string;
  url?: string;
  description?: string;
}

interface ListItemJsonLd {
  '@type': 'ListItem';
  position?: number;
  name?: string;
  description?: string;
  item?: string;
}

interface ItemListJsonLd extends BaseJsonLd {
  '@type': 'ItemList';
  '@id'?: string;
  name?: string;
  itemListOrder?: ItemListOrder;
  itemListElement?: Array<WebApplicationJsonLd | ListItemJsonLd>;
}

interface JsonLdGraph {
  '@context': JsonLdContext;
  '@graph': Array<BaseJsonLd & { '@id'?: string }>;
}

function createJsonLd<T extends BaseJsonLd>(
  type: string,
  data: Omit<T, '@type' | '@context'>,
  context: JsonLdContext = 'https://schema.org',
): T {
  return {
    '@context': context,
    '@type': type,
    ...data,
  } as T;
}

function createOrganization(
  data: Omit<OrganizationJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): OrganizationJsonLd {
  return createJsonLd<OrganizationJsonLd>('Organization', data, context);
}

function createWebSite(
  data: Omit<WebSiteJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): WebSiteJsonLd {
  return createJsonLd<WebSiteJsonLd>('WebSite', data, context);
}

function createArticle(
  type: ArticleType,
  data: Omit<ArticleJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): ArticleJsonLd {
  return createJsonLd<ArticleJsonLd>(type, data, context);
}

function createBreadcrumbList(
  items: Array<{ name: string; item?: string }>,
  context: JsonLdContext = 'https://schema.org',
): BreadcrumbListJsonLd {
  const itemListElement = items.map((item, index) =>
    createListItem({
      position: index + 1,
      name: item.name,
      ...(item.item && { item: item.item }),
    }),
  );

  return createJsonLd<BreadcrumbListJsonLd>('BreadcrumbList', { itemListElement }, context);
}

function createFAQPage(
  questions: Array<{ question: string; answer: string }>,
  context: JsonLdContext = 'https://schema.org',
  id?: string,
): FAQPageJsonLd {
  const mainEntity = questions.map((qa) => ({
    '@type': 'Question' as const,
    name: qa.question,
    acceptedAnswer: {
      '@type': 'Answer' as const,
      text: qa.answer,
    },
  }));

  return createJsonLd<FAQPageJsonLd>('FAQPage', { ...(id && { '@id': id }), mainEntity }, context);
}

function createProduct(
  data: Omit<ProductJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): ProductJsonLd {
  return createJsonLd<ProductJsonLd>('Product', data, context);
}

function createPerson(
  data: Omit<PersonJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): PersonJsonLd {
  return createJsonLd<PersonJsonLd>('Person', data, context);
}

function createSoftwareApplication(
  data: Omit<SoftwareApplicationJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): SoftwareApplicationJsonLd {
  return createJsonLd<SoftwareApplicationJsonLd>('SoftwareApplication', data, context);
}

function createWebPage(
  data: Omit<WebPageJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): WebPageJsonLd {
  return createJsonLd<WebPageJsonLd>('WebPage', data, context);
}

function createWebApplication(
  data: Omit<WebApplicationJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): WebApplicationJsonLd {
  return createJsonLd<WebApplicationJsonLd>('WebApplication', data, context);
}

function createListItem(data: Omit<ListItemJsonLd, '@type'>): ListItemJsonLd {
  return {
    '@type': 'ListItem',
    ...data,
  };
}

function createItemList(
  data: Omit<ItemListJsonLd, '@context' | '@type'>,
  context: JsonLdContext = 'https://schema.org',
): ItemListJsonLd {
  return createJsonLd<ItemListJsonLd>('ItemList', data, context);
}

function createWebApplicationItemList(
  id: string,
  name: string,
  items: Array<{ name: string; description: string; url?: string }>,
  options?: {
    itemListOrder?: ItemListOrder;
    context?: JsonLdContext;
  },
): ItemListJsonLd {
  const itemListElement = items.map((item) =>
    createWebApplication(
      {
        name: item.name,
        description: item.description,
        ...(item.url && { url: item.url }),
      },
      options?.context,
    ),
  );

  return createItemList(
    {
      '@id': id,
      name,
      itemListOrder: options?.itemListOrder || 'Unordered',
      itemListElement,
    },
    options?.context,
  );
}

function createListItemItemList(
  id: string,
  name: string,
  items: Array<{ name: string; description?: string }>,
  options?: {
    itemListOrder?: ItemListOrder;
    context?: JsonLdContext;
  },
): ItemListJsonLd {
  const itemListElement = items.map((item, index) =>
    createListItem({
      position: index + 1,
      name: item.name,
      ...(item.description && { description: item.description }),
    }),
  );

  return createItemList(
    {
      '@id': id,
      name,
      itemListOrder: options?.itemListOrder || 'Unordered',
      itemListElement,
    },
    options?.context,
  );
}

function createGraph(
  entities: Array<BaseJsonLd & { '@id'?: string }>,
  context: JsonLdContext = 'https://schema.org',
): JsonLdGraph {
  return {
    '@context': context,
    '@graph': entities,
  };
}

function toString(jsonLd: BaseJsonLd | JsonLdGraph): string {
  return JSON.stringify(jsonLd);
}

export default {
  createOrganization,
  createWebSite,
  createArticle,
  createBreadcrumbList,
  createFAQPage,
  createProduct,
  createPerson,
  createSoftwareApplication,
  createWebPage,
  createWebApplication,
  createListItem,
  createItemList,
  createWebApplicationItemList,
  createListItemItemList,
  createGraph,
  toString,
};
